/**
 * @fileoverview User Settings Page
 *
 * Allows users to configure accumulation control, review behavior, and notifications.
 * Implements Option D (Hybrid Approach) settings from pause-accumulation-design.md.
 *
 * @module routes/app/settings
 */

import { createFileRoute } from '@tanstack/react-router'
import { useQuery as useConvexQuery, useMutation } from 'convex/react'
import { api } from 'convex/_generated/api'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useState, useEffect } from 'react'
import { Save, RotateCcw, CheckCircle, AlertCircle, X } from 'lucide-react'
import { usePostHog } from '@/hooks/usePostHog'
import { Tooltip } from '@/components/Tooltip'

export const Route = createFileRoute('/app/settings')({
  component: SettingsPage,
})

function SettingsPage() {
  const currentUser = useCurrentUser()

  if (currentUser === undefined) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-sky-blue text-xl font-mono">Loading...</div>
      </div>
    )
  }

  if (currentUser === null) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-trail-tan text-xl font-mono">Please sign in to access settings</div>
      </div>
    )
  }

  return <SettingsContent userId={currentUser._id} />
}

function SettingsContent({ userId }: { userId: string }) {
  const { trackEvent } = usePostHog()
  const userSettings = useConvexQuery(api.userSettings.getUserSettings, {
    userId: userId as any,
  })
  const defaultSettings = useConvexQuery(api.userSettings.getDefaultSettings)
  const updateSettings = useMutation(api.userSettings.updateUserSettings)
  const resetToDefaults = useMutation(api.userSettings.resetToDefaults)

  const [isSaving, setIsSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [isDirty, setIsDirty] = useState(false)

  // Form state
  const [maxDailyFlashcards, setMaxDailyFlashcards] = useState(50)
  const [vacationModeThreshold, setVacationModeThreshold] = useState(3)
  const [resetImmatureCards, setResetImmatureCards] = useState(true)
  const [resetMatureCards, setResetMatureCards] = useState(false)
  const [autoPlayAudio, setAutoPlayAudio] = useState(true)
  const [showSpacedRepetitionStats, setShowSpacedRepetitionStats] = useState(true)
  const [enableKeyboardShortcuts, setEnableKeyboardShortcuts] = useState(true)
  const [dailyReminderEnabled, setDailyReminderEnabled] = useState(false)
  const [dailyReminderTime, setDailyReminderTime] = useState('09:00')
  const [catchupModeNotifications, setCatchupModeNotifications] = useState(true)

  // Initialize form from settings
  useEffect(() => {
    if (userSettings) {
      setMaxDailyFlashcards(userSettings.maxDailyFlashcards)
      setVacationModeThreshold(userSettings.vacationModeThreshold)
      setResetImmatureCards(userSettings.resetImmatureCards)
      setResetMatureCards(userSettings.resetMatureCards)
      setAutoPlayAudio(userSettings.autoPlayAudio ?? true)
      setShowSpacedRepetitionStats(userSettings.showSpacedRepetitionStats ?? true)
      setEnableKeyboardShortcuts(userSettings.enableKeyboardShortcuts ?? true)
      setDailyReminderEnabled(userSettings.dailyReminderEnabled ?? false)
      setDailyReminderTime(userSettings.dailyReminderTime ?? '09:00')
      setCatchupModeNotifications(userSettings.catchupModeNotifications ?? true)
      setIsDirty(false)

      // Track settings page view with current values
      trackEvent('settings_viewed', {
        maxDailyFlashcards: userSettings.maxDailyFlashcards,
        vacationModeThreshold: userSettings.vacationModeThreshold,
        resetImmatureCards: userSettings.resetImmatureCards,
        resetMatureCards: userSettings.resetMatureCards,
      })
    }
  }, [userSettings, trackEvent])

  // Auto-dismiss notifications
  useEffect(() => {
    if (saveSuccess) {
      const timer = setTimeout(() => setSaveSuccess(false), 3000)
      return () => clearTimeout(timer)
    }
  }, [saveSuccess])

  useEffect(() => {
    if (saveError) {
      const timer = setTimeout(() => setSaveError(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [saveError])

  const handleSave = async () => {
    setIsSaving(true)
    setSaveError(null)

    try {
      await updateSettings({
        userId: userId as any,
        settings: {
          maxDailyFlashcards,
          vacationModeThreshold,
          resetImmatureCards,
          resetMatureCards,
          autoPlayAudio,
          showSpacedRepetitionStats,
          enableKeyboardShortcuts,
          dailyReminderEnabled,
          dailyReminderTime,
          catchupModeNotifications,
        },
      })

      // Track settings save with new values
      trackEvent('settings_saved', {
        maxDailyFlashcards,
        vacationModeThreshold,
        resetImmatureCards,
        resetMatureCards,
      })

      setSaveSuccess(true)
      setIsDirty(false)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      setSaveError(`Failed to save settings: ${errorMessage}`)
    } finally {
      setIsSaving(false)
    }
  }

  const handleReset = async () => {
    if (!confirm('Reset all settings to defaults? This cannot be undone.')) {
      return
    }

    setIsSaving(true)
    setSaveError(null)

    try {
      await resetToDefaults({
        userId: userId as any,
      })

      // Track settings reset
      trackEvent('settings_reset_to_defaults', {
        maxDailyFlashcards: defaultSettings?.maxDailyFlashcards ?? 50,
        vacationModeThreshold: defaultSettings?.vacationModeThreshold ?? 3,
      })

      setSaveSuccess(true)
      setIsDirty(false)
      // Form will auto-update via useEffect when settings reload
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      setSaveError(`Failed to reset settings: ${errorMessage}`)
    } finally {
      setIsSaving(false)
    }
  }

  if (!userSettings || !defaultSettings) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-sky-blue text-xl font-mono">Loading settings...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-black text-white py-8 px-4">
      {/* Success Notification */}
      {saveSuccess && (
        <div className="fixed top-4 right-4 max-w-md z-50 animate-in slide-in-from-top">
          <div className="terminal-border border-terminal-green bg-black p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-terminal-green flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-mono font-bold text-terminal-green">Settings Saved</h3>
                <p className="font-mono text-sm text-trail-tan">Your preferences have been updated</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Notification */}
      {saveError && (
        <div className="fixed top-4 right-4 max-w-md z-50 animate-in slide-in-from-top">
          <div className="terminal-border border-red-500 bg-black p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3 className="font-mono font-bold text-red-500 mb-1">Error</h3>
                <p className="font-mono text-sm text-red-400 break-words">{saveError}</p>
              </div>
              <button
                onClick={() => setSaveError(null)}
                className="flex-shrink-0 text-red-500 hover:text-red-400 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-sky-blue font-mono mb-2">[USER SETTINGS]</h1>
          <p className="text-trail-tan font-mono">
            Configure your review behavior and accumulation control preferences
          </p>
        </div>

        {/* Accumulation Control Section */}
        <div className="mb-8 terminal-border border-terminal-green p-6">
          <h2 className="text-xl font-bold text-terminal-green font-mono mb-4">
            [ACCUMULATION CONTROL]
          </h2>
          <p className="text-trail-tan font-mono text-sm mb-6">
            Manage how flashcards accumulate when you miss review days
          </p>

          {/* Max Daily Flashcards */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <label className="text-sky-blue font-mono font-bold">
                Maximum Daily Flashcards
              </label>
              <Tooltip
                content="Limits how many flashcards you review per day, even if hundreds are overdue. Prevents overwhelming catch-up sessions after absences. Example: If 150 cards are due, you'll see 50 today, 50 tomorrow, 50 the next day."
                position="top"
                maxWidth="max-w-sm"
              />
            </div>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="10"
                max="200"
                step="10"
                value={maxDailyFlashcards}
                onChange={(e) => {
                  setMaxDailyFlashcards(parseInt(e.target.value))
                  setIsDirty(true)
                }}
                className="flex-1 h-2 bg-black border-2 border-sky-blue rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-terminal-green [&::-webkit-slider-thumb]:rounded-full"
              />
              <span className="text-terminal-green font-mono font-bold text-lg w-12 text-right">
                {maxDailyFlashcards}
              </span>
            </div>
            <p className="text-trail-tan font-mono text-xs mt-2">
              Cap daily reviews to prevent overwhelm (default: {defaultSettings.maxDailyFlashcards})
            </p>
          </div>

          {/* Vacation Mode Threshold */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <label className="text-sky-blue font-mono font-bold">
                Vacation Mode Threshold (days)
              </label>
              <Tooltip
                content="Number of consecutive missed days before 'smart reset' triggers for immature cards. Set to 3 days = weekend breaks are OK. Set to 7 days = full week allowed before reset. Lower values (1-2) make the system more strict."
                position="top"
                maxWidth="max-w-sm"
              />
            </div>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="1"
                max="14"
                step="1"
                value={vacationModeThreshold}
                onChange={(e) => {
                  setVacationModeThreshold(parseInt(e.target.value))
                  setIsDirty(true)
                }}
                className="flex-1 h-2 bg-black border-2 border-sky-blue rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-terminal-green [&::-webkit-slider-thumb]:rounded-full"
              />
              <span className="text-terminal-green font-mono font-bold text-lg w-12 text-right">
                {vacationModeThreshold}
              </span>
            </div>
            <p className="text-trail-tan font-mono text-xs mt-2">
              Days missed before smart reset kicks in (default: {defaultSettings.vacationModeThreshold})
            </p>
          </div>

          {/* Smart Reset Options */}
          <div className="mb-4">
            <div className="flex items-start gap-2">
              <label className="flex items-center gap-3 cursor-pointer group flex-1">
                <input
                  type="checkbox"
                  checked={resetImmatureCards}
                  onChange={(e) => {
                    setResetImmatureCards(e.target.checked)
                    setIsDirty(true)
                  }}
                  className="w-5 h-5 bg-black border-2 border-sky-blue checked:bg-terminal-green checked:border-terminal-green cursor-pointer"
                />
                <span className="text-sky-blue font-mono group-hover:text-terminal-green transition-colors">
                  Reset immature cards (interval &lt; 21 days)
                </span>
              </label>
              <Tooltip
                content="Immature cards are those you're still learning (review interval less than 3 weeks). If you miss reviews beyond the vacation threshold, these cards are marked for reset. This prevents reviewing cards you've likely forgotten, saving you time and frustration."
                position="left"
                maxWidth="max-w-xs"
              />
            </div>
            <p className="text-trail-tan font-mono text-xs mt-1 ml-8">
              Restart learning for cards you likely forgot (recommended)
            </p>
          </div>

          <div>
            <div className="flex items-start gap-2">
              <label className="flex items-center gap-3 cursor-pointer group flex-1">
                <input
                  type="checkbox"
                  checked={resetMatureCards}
                  onChange={(e) => {
                    setResetMatureCards(e.target.checked)
                    setIsDirty(true)
                  }}
                  className="w-5 h-5 bg-black border-2 border-sky-blue checked:bg-terminal-green checked:border-terminal-green cursor-pointer"
                />
                <span className="text-sky-blue font-mono group-hover:text-terminal-green transition-colors">
                  Reset mature cards (interval ≥ 21 days)
                </span>
              </label>
              <Tooltip
                content="Mature cards are well-learned cards with review intervals of 3+ weeks. These cards typically have high retention rates. Resetting them means throwing away months of learning progress. Only enable if you've had a multi-month break and truly forgot the content."
                position="left"
                maxWidth="max-w-xs"
              />
            </div>
            <p className="text-trail-tan font-mono text-xs mt-1 ml-8">
              Also restart well-learned cards (not recommended)
            </p>
          </div>
        </div>

        {/* Review Behavior Section */}
        <div className="mb-8 terminal-border border-sky-blue p-6">
          <h2 className="text-xl font-bold text-sky-blue font-mono mb-4">[REVIEW BEHAVIOR]</h2>

          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={autoPlayAudio}
                onChange={(e) => {
                  setAutoPlayAudio(e.target.checked)
                  setIsDirty(true)
                }}
                className="w-5 h-5 bg-black border-2 border-sky-blue checked:bg-terminal-green checked:border-terminal-green cursor-pointer"
              />
              <span className="text-sky-blue font-mono group-hover:text-terminal-green transition-colors">
                Auto-play audio on flashcard reveal
              </span>
            </label>

            <div className="flex items-start gap-2">
              <label className="flex items-center gap-3 cursor-pointer group flex-1">
                <input
                  type="checkbox"
                  checked={showSpacedRepetitionStats}
                  onChange={(e) => {
                    setShowSpacedRepetitionStats(e.target.checked)
                    setIsDirty(true)
                  }}
                  className="w-5 h-5 bg-black border-2 border-sky-blue checked:bg-terminal-green checked:border-terminal-green cursor-pointer"
                />
                <span className="text-sky-blue font-mono group-hover:text-terminal-green transition-colors">
                  Show spaced repetition stats (ease, interval, repetitions)
                </span>
              </label>
              <Tooltip
                content="Technical details shown during review: 'Ease' is difficulty multiplier (higher = easier card), 'Interval' is days until next review (grows with good performance), 'Repetitions' is total review count. Useful for understanding the algorithm, but not essential for learning."
                position="left"
                maxWidth="max-w-sm"
              />
            </div>

            <label className="flex items-center gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={enableKeyboardShortcuts}
                onChange={(e) => {
                  setEnableKeyboardShortcuts(e.target.checked)
                  setIsDirty(true)
                }}
                className="w-5 h-5 bg-black border-2 border-sky-blue checked:bg-terminal-green checked:border-terminal-green cursor-pointer"
              />
              <span className="text-sky-blue font-mono group-hover:text-terminal-green transition-colors">
                Enable keyboard shortcuts (0-5 for grading)
              </span>
            </label>
          </div>
        </div>

        {/* Notifications Section */}
        <div className="mb-8 terminal-border border-trail-tan p-6">
          <h2 className="text-xl font-bold text-trail-tan font-mono mb-4">[NOTIFICATIONS]</h2>

          <div className="space-y-4">
            <div>
              <label className="flex items-center gap-3 cursor-pointer group mb-2">
                <input
                  type="checkbox"
                  checked={dailyReminderEnabled}
                  onChange={(e) => {
                    setDailyReminderEnabled(e.target.checked)
                    setIsDirty(true)
                  }}
                  className="w-5 h-5 bg-black border-2 border-trail-tan checked:bg-terminal-green checked:border-terminal-green cursor-pointer"
                />
                <span className="text-trail-tan font-mono group-hover:text-terminal-green transition-colors">
                  Daily review reminder
                </span>
              </label>
              {dailyReminderEnabled && (
                <div className="ml-8">
                  <input
                    type="time"
                    value={dailyReminderTime}
                    onChange={(e) => {
                      setDailyReminderTime(e.target.value)
                      setIsDirty(true)
                    }}
                    className="px-3 py-2 bg-black border-2 border-trail-tan text-terminal-green font-mono"
                  />
                </div>
              )}
            </div>

            <label className="flex items-center gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={catchupModeNotifications}
                onChange={(e) => {
                  setCatchupModeNotifications(e.target.checked)
                  setIsDirty(true)
                }}
                className="w-5 h-5 bg-black border-2 border-trail-tan checked:bg-terminal-green checked:border-terminal-green cursor-pointer"
              />
              <span className="text-trail-tan font-mono group-hover:text-terminal-green transition-colors">
                Show catch-up mode progress notifications
              </span>
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 justify-end">
          <button
            onClick={handleReset}
            disabled={isSaving}
            className="flex items-center gap-2 px-6 py-3 bg-black border-2 border-trail-tan text-trail-tan hover:bg-trail-tan hover:text-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-mono font-bold"
          >
            <RotateCcw className="w-4 h-4" />
            <span>Reset to Defaults</span>
          </button>

          <button
            onClick={handleSave}
            disabled={isSaving || !isDirty}
            className="flex items-center gap-2 px-6 py-3 bg-black border-2 border-terminal-green text-terminal-green hover:bg-terminal-green hover:text-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-mono font-bold"
          >
            {isSaving ? (
              <>
                <Save className="w-4 h-4 animate-pulse" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Save Settings</span>
              </>
            )}
          </button>
        </div>

        {/* Info Box */}
        <div className="mt-8 terminal-border border-sky-blue bg-black/50 p-4">
          <p className="text-sky-blue font-mono text-xs">
            <strong>[INFO]</strong> These settings implement Option D (Hybrid Approach) for accumulation
            control. Your mature cards (interval ≥ 21 days) are preserved by default to maintain your
            learning progress, while immature cards are reset after extended absences to prevent wasted
            reviews.
          </p>
        </div>
      </div>
    </div>
  )
}
